import React, { useCallback } from 'react';
import { classNames } from 'libs/growth-platform-brand-system-v2/src/utils/style';
import {
  fontBody,
  fontCaption,
  fontBodyMini,
  fontCaptionMedium,
  decorationUnderlineDotted,
  fontMegaBodyMedium,
} from 'libs/growth-platform-brand-system-v2/src/styles/typographyNew';
import { useQueryParam } from '../scripts/hooks/useQueryParam';
import { filterPlural } from '../scripts/filterPlural';
import { NumberEmployeeInput } from '../types';
import { translation } from '../scripts/translation';
import { SiteLocale } from 'apps/website/types/dato';
import { Popover } from 'libs/growth-platform-brand-system-v2/src/components/Popover';
import { Image } from 'libs/growth-platform-brand-system-v2/src/components/Image';
import { shortcode } from 'apps/website/src/utils/Shortcode';

export const MIN_VALUE_NUMBER_EMPLOYEE = 1;
export const MAX_VALUE_NUMBER_EMPLOYEE = 999;

const Input = ({
  numberEmployeeInput,
  locale,
}: {
  numberEmployeeInput: NumberEmployeeInput;
  locale: SiteLocale;
}) => {
  const [nbEmployeesInput, setNbEmployeesInput] = useQueryParam(
    'nbEmployee',
    MIN_VALUE_NUMBER_EMPLOYEE.toString(),
  );

  const _nbEmployees = nbEmployeesInput
    ? +nbEmployeesInput
    : MIN_VALUE_NUMBER_EMPLOYEE;

  const updateNbEmployeesInput = useCallback((value: number) => {
    if (!value) {
      setNbEmployeesInput('');
      return;
    }
    if (value < MIN_VALUE_NUMBER_EMPLOYEE) {
      setNbEmployeesInput(MIN_VALUE_NUMBER_EMPLOYEE.toString());
      return;
    }

    if (value > MAX_VALUE_NUMBER_EMPLOYEE) {
      setNbEmployeesInput(MAX_VALUE_NUMBER_EMPLOYEE.toString());
      return;
    }
    setNbEmployeesInput(value.toString());
  }, [setNbEmployeesInput]);

  const isMin =
    !!nbEmployeesInput && _nbEmployees === MIN_VALUE_NUMBER_EMPLOYEE;

  const isMax = _nbEmployees === MAX_VALUE_NUMBER_EMPLOYEE;

  const tooltip = numberEmployeeInput.tooltip;
  const tooltipTitle = tooltip?.title;
  const tooltipText = tooltip?.text;
  const tooltipImage = tooltip?.image;
  const hasTooltip = tooltipTitle || tooltipText || tooltipImage;

  const label = filterPlural(
    _nbEmployees,
    numberEmployeeInput?.label,
    numberEmployeeInput?.labelPlural,
  );

  return (
    <>
      {numberEmployeeInput.title && (
        <div className={classNames(fontMegaBodyMedium, 'px-[9px]')}>
          {numberEmployeeInput.title}
        </div>
      )}

      <div
        className={classNames(
          'SettingsPanel__Input',
          'relative mx-auto w-full gap-[12px] xl:mx-0 xl:flex xl:w-auto',
        )}
      >
        <div
          className={classNames(
            'flex gap-[6px] rounded-[14px] bg-[#F9F9FB] p-[6px]',
          )}
        >
          <button
            className="flex w-[42px] items-center justify-center md:w-[92px] lg:w-[42px]"
            onClick={() => {
              if (!nbEmployeesInput) setNbEmployeesInput('1');
              else updateNbEmployeesInput(_nbEmployees - 1);
            }}
            aria-label={translation[locale]?.['aria.numberEmployeesInput.min']}
            disabled={isMin}
            tabIndex={isMin ? -1 : 0}
          >
            <svg width="14" height="2" viewBox="0 0 14 2" fill="none">
              <path d="M13.999 2H-0.000976562V0H13.999V2Z" fill="#1C2024" />
            </svg>
          </button>

          <input
            type="number"
            value={nbEmployeesInput ? _nbEmployees : ''}
            min={MIN_VALUE_NUMBER_EMPLOYEE}
            max={MAX_VALUE_NUMBER_EMPLOYEE}
            aria-describedby="collaborators-announcement"
            aria-invalid={
              _nbEmployees < MIN_VALUE_NUMBER_EMPLOYEE ||
              _nbEmployees > MAX_VALUE_NUMBER_EMPLOYEE
            }
            aria-label={
              translation[locale]?.['aria.numberEmployeesInput.action']
            }
            onChange={(e) => {
              const value = e.target.value;
              if (!isNaN(+value)) updateNbEmployeesInput(+value);
              else setNbEmployeesInput('');
            }}
            className={classNames(
              'border border-[#F0F0F3] px-[12px] py-[15px] text-left',
              'text-[24px] leading-[34px]',
              'w-full rounded-[8px] xl:w-[92px]',
            )}
          />

          {/* Message for screen reader */}
          <div
            aria-live="polite"
            id="collaborators-announcement"
            className="sr-only"
            dangerouslySetInnerHTML={{
              __html:
                shortcode(
                  filterPlural(
                    _nbEmployees,
                    translation[locale]?.['aria.numberEmployeesInput'],
                    translation[locale]?.['aria.numberEmployeesInput.plural'],
                  ) || '',
                  locale,
                )
                  .pricingPage.applyNbEmployee({ nbEmployee: _nbEmployees })
                  .toString() || '',
            }}
          />

          <button
            className="flex w-[42px] items-center justify-center md:w-[92px] lg:w-[42px]"
            onClick={() => {
              if (!nbEmployeesInput) setNbEmployeesInput('1');
              else updateNbEmployeesInput(_nbEmployees + 1);
            }}
            aria-label={translation[locale]?.['aria.numberEmployeesInput.max']}
            disabled={isMax}
            tabIndex={isMax ? -1 : 0}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M13.999 8H7.99902V14H5.99902V8H-0.000976562V6H5.99902V0H7.99902V6H13.999V8Z"
                fill="#1C2024"
              />
            </svg>
          </button>
        </div>

        {/* Text hidden for screen readers */}
        <div
          aria-hidden="true"
          className={classNames(
            'absolute right-0 top-0',
            'xl:relative xl:right-auto xl:top-auto',
            'flex items-center',
            'right-[112px] h-full xl:h-auto',
            fontBody,
          )}
        >
          {hasTooltip ? (
            <Popover
              label={
                <div
                  className={classNames(
                    decorationUnderlineDotted,
                    'text-[#80838D]',
                    fontBodyMini,
                  )}
                >
                  {label}
                </div>
              }
              className="z-50"
              position="top"
            >
              <div
                className={classNames('flex flex-col gap-[12px] md:w-[280px]')}
              >
                {tooltipTitle && (
                  <div className={classNames(fontCaptionMedium)}>
                    {tooltipTitle}
                  </div>
                )}
                {tooltipImage && (
                  <Image
                    src={tooltipImage.url}
                    alt={tooltipImage.alt}
                    pixelDensity={'image'}
                    width={tooltipImage.width}
                    height={tooltipImage.height}
                    sizes={'284px'}
                    className={classNames('w-full rounded-[8px] bg-[#F9F9FB]')}
                  />
                )}

                {tooltipText && (
                  <div
                    className={classNames(fontCaption)}
                    dangerouslySetInnerHTML={{ __html: tooltipText }}
                  />
                )}
              </div>
            </Popover>
          ) : (
            label
          )}
        </div>
      </div>
    </>
  );
};

export default Input;
